import type { FormatParser, ParsedPoint, ParsedTrack, TrackWarning } from './types';
import { ParseError } from './types';
import { kmhToKnots, metresPerSecondToKnots, metresToFeet, parseLooseNumber } from './units';

/**
 * GeoJSON: the escape hatch.
 *
 * `DATA-SOURCES.md` §2 lists this as "anything", and that is the point — if a
 * track can be got out of some tool as GeoJSON, it can get in here without
 * waiting for a bespoke parser. Two shapes are accepted:
 *
 *  - a `LineString` or `MultiLineString`, which is geometry with no time, and
 *  - a `FeatureCollection` of `Point` features, where the per-feature
 *    properties are where time, altitude and speed can actually live.
 *
 * GeoJSON coordinates are `[lon, lat, elevation]` in that order (RFC 7946 is
 * unambiguous, and getting it backwards is the classic way to put a Whakatane
 * flight in Mongolia) and the elevation is metres.
 *
 * Property *names*, unlike the geometry, are nobody's standard, so each field
 * is looked up under the spellings tools actually use.
 */

const TIME_KEYS = ['time', 'timestamp', 'utc', 'datetime', 'date_time', 't'] as const;
const ALT_FT_KEYS = ['altitude', 'alt', 'altitude_ft', 'alt_ft', 'baro_altitude_ft'] as const;
const ALT_M_KEYS = ['altitude_m', 'alt_m', 'elevation', 'ele', 'baro_altitude'] as const;
const SPEED_KT_KEYS = [
  'speed',
  'gs',
  'groundspeed',
  'ground_speed',
  'speed_kt',
  'velocity',
] as const;
const SPEED_MS_KEYS = ['speed_ms', 'velocity_ms', 'gs_ms'] as const;
const SPEED_KMH_KEYS = ['speed_kmh', 'speed_kph'] as const;
const HEADING_KEYS = ['heading', 'track', 'course', 'direction', 'bearing'] as const;

type Properties = Record<string, unknown>;

/** A feature's `properties`, or an empty block when it has none. */
function propertiesOf(value: unknown): Properties {
  return typeof value === 'object' && value !== null ? (value as Properties) : {};
}

/**
 * Case- and separator-insensitive property names, built once per block.
 *
 * Cached because each fix is looked up six times (time, two altitude spellings,
 * three speed ones, heading), and a `FeatureCollection` of 20,000 points would
 * otherwise rebuild the same map 120,000 times. A `LineString` shares one
 * properties object across every vertex, so there the cache is one entry.
 */
const NORMALISED_KEYS = new WeakMap<Properties, Map<string, unknown>>();

function normalisedKeys(properties: Properties): Map<string, unknown> {
  const cached = NORMALISED_KEYS.get(properties);
  if (cached !== undefined) {
    return cached;
  }
  const normalised = new Map<string, unknown>();
  for (const [key, value] of Object.entries(properties)) {
    normalised.set(key.toLowerCase().replace(/[^a-z0-9]/g, ''), value);
  }
  NORMALISED_KEYS.set(properties, normalised);
  return normalised;
}

function lookup(properties: Properties, keys: readonly string[]): unknown {
  // So that `Altitude`, `altitude` and `ALT_FT` all resolve without listing
  // every spelling of every key.
  const normalised = normalisedKeys(properties);
  for (const key of keys) {
    const value = normalised.get(key.replace(/[^a-z0-9]/g, ''));
    if (value !== undefined && value !== null) {
      return value;
    }
  }
  return undefined;
}

function numberFrom(value: unknown): number {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : Number.NaN;
  }
  if (typeof value === 'string') {
    return parseLooseNumber(value);
  }
  return Number.NaN;
}

/** An epoch number, or a parseable date string. */
function timeFrom(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    // Seconds if it looks like seconds; milliseconds if it looks like those.
    return value > 1e11 ? value : value * 1000;
  }
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
    const epoch = parseLooseNumber(value);
    if (Number.isFinite(epoch) && epoch > 0) {
      return epoch > 1e11 ? epoch : epoch * 1000;
    }
  }
  return Number.NaN;
}

function altitudeFeetFrom(properties: Properties): number {
  const feet = numberFrom(lookup(properties, ALT_FT_KEYS));
  if (Number.isFinite(feet)) {
    return feet;
  }
  const metres = numberFrom(lookup(properties, ALT_M_KEYS));
  return Number.isFinite(metres) ? metresToFeet(metres) : Number.NaN;
}

function speedKnotsFrom(properties: Properties): number {
  const knots = numberFrom(lookup(properties, SPEED_KT_KEYS));
  if (Number.isFinite(knots)) {
    return knots;
  }
  const mps = numberFrom(lookup(properties, SPEED_MS_KEYS));
  if (Number.isFinite(mps)) {
    return metresPerSecondToKnots(mps);
  }
  const kmh = numberFrom(lookup(properties, SPEED_KMH_KEYS));
  return Number.isFinite(kmh) ? kmhToKnots(kmh) : Number.NaN;
}

/** `[lon, lat, elevationMetres?]`, per RFC 7946. */
function pointFromPosition(position: unknown, properties: Properties): ParsedPoint | undefined {
  if (!Array.isArray(position) || position.length < 2) {
    return undefined;
  }
  const lon = numberFrom(position[0]);
  const lat = numberFrom(position[1]);
  const elevation = position.length > 2 ? numberFrom(position[2]) : Number.NaN;
  const propertyAlt = altitudeFeetFrom(properties);

  return {
    tMs: timeFrom(lookup(properties, TIME_KEYS)),
    lat,
    lon,
    altFt: Number.isFinite(propertyAlt)
      ? propertyAlt
      : Number.isFinite(elevation)
        ? metresToFeet(elevation)
        : Number.NaN,
    gsKt: speedKnotsFrom(properties),
    trackDeg: numberFrom(lookup(properties, HEADING_KEYS)),
  };
}

interface Geometry {
  type?: unknown;
  coordinates?: unknown;
  geometries?: unknown;
}

function collectFromGeometry(
  geometry: Geometry,
  properties: Properties,
  into: ParsedPoint[],
): void {
  const type = typeof geometry.type === 'string' ? geometry.type : '';

  switch (type) {
    case 'Point': {
      const point = pointFromPosition(geometry.coordinates, properties);
      if (point !== undefined) {
        into.push(point);
      }
      break;
    }
    case 'LineString':
    case 'MultiPoint': {
      if (Array.isArray(geometry.coordinates)) {
        for (const position of geometry.coordinates) {
          const point = pointFromPosition(position, properties);
          if (point !== undefined) {
            // A shared `properties` block cannot give each vertex its own time,
            // so a LineString's points are deliberately timeless.
            into.push({ ...point, tMs: Number.NaN });
          }
        }
      }
      break;
    }
    case 'MultiLineString': {
      if (Array.isArray(geometry.coordinates)) {
        for (const line of geometry.coordinates) {
          collectFromGeometry({ type: 'LineString', coordinates: line }, properties, into);
        }
      }
      break;
    }
    case 'GeometryCollection': {
      if (Array.isArray(geometry.geometries)) {
        for (const child of geometry.geometries) {
          collectFromGeometry(child as Geometry, properties, into);
        }
      }
      break;
    }
    default:
      break;
  }
}

function parseGeoJson(bytes: Uint8Array): ParsedTrack {
  let root: unknown;
  try {
    root = JSON.parse(new TextDecoder().decode(bytes));
  } catch (cause) {
    throw new ParseError('This file is not valid JSON.', [
      cause instanceof Error ? cause.message : String(cause),
    ]);
  }
  if (typeof root !== 'object' || root === null) {
    throw new ParseError('This JSON file is not GeoJSON: the top level is not an object.');
  }

  const points: ParsedPoint[] = [];
  const document = root as { type?: unknown; features?: unknown; geometry?: unknown };
  const type = typeof document.type === 'string' ? document.type : '';
  let shape = type;

  if (type === 'FeatureCollection' && Array.isArray(document.features)) {
    for (const feature of document.features) {
      const entry = feature as { geometry?: unknown; properties?: unknown };
      if (typeof entry.geometry === 'object' && entry.geometry !== null) {
        collectFromGeometry(entry.geometry, propertiesOf(entry.properties), points);
      }
    }
    shape = `FeatureCollection of ${String(document.features.length)} features`;
  } else if (type === 'Feature') {
    const properties = propertiesOf((document as { properties?: unknown }).properties);
    if (typeof document.geometry === 'object' && document.geometry !== null) {
      collectFromGeometry(document.geometry, properties, points);
    }
  } else {
    collectFromGeometry(document, {}, points);
  }

  if (points.length === 0) {
    throw new ParseError(
      'No coordinates found in this GeoJSON. Expected a LineString, or a FeatureCollection of Points.',
    );
  }

  const warnings: TrackWarning[] = [];
  return {
    source: 'geojson',
    points,
    // GeoJSON elevation is a height above the ellipsoid, which is a geometric
    // measurement, but a tool re-exporting ADS-B into it may well have put a
    // barometric figure there. Unknowable from the file, so unclaimed.
    altitudeRef: 'unknown',
    warnings,
    notes: [`${shape === '' ? 'GeoJSON' : shape}, ${String(points.length)} positions`],
  };
}

export const geoJsonParser: FormatParser = {
  format: 'geojson',
  label: 'GeoJSON',
  extensions: ['.geojson', '.json'],
  sniff(text) {
    const head = text.slice(0, 4000);
    return (
      /"type"\s*:\s*"(?:FeatureCollection|Feature|LineString|MultiLineString|GeometryCollection|Point)"/.test(
        head,
      ) && head.trimStart().startsWith('{')
    );
  },
  parse: parseGeoJson,
};
